import { Banner } from "@design";
import { CircleAlert, Gauge } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { BannerAction } from "@/components/BannerAction";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { budgetLevel, formatCost } from "@/features/settings/usage/spend";
import { formatDate } from "@/i18n/format";

const DISMISS_KEY = "anthm.budgetWarningDismissed";

/** Dismissal lasts the browser session and only for this budget period (keyed by its reset date). */
function readDismissed(resetsAt: string) {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === resetsAt;
  } catch {
    return false;
  }
}

/**
 * App banner as the monthly AI budget runs out (frontend/'s BudgetBanner levels): a dismissible warning from 80%,
 * and a persistent error once it's reached (new AI requests are refused until the reset).
 */
export function BudgetBanner() {
  const { t } = useTranslation("shell", { keyPrefix: "budgetBanner" });
  const navigate = useNavigate();
  const budget = useCurrentUser().data?.budget;
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  if (!budget) return null;
  const level = budgetLevel(budget);
  if (level === "ok") return null;

  const date = formatDate(budget.resetsAt, { month: "long", day: "numeric", timeZone: "UTC" });
  const action = <BannerAction onClick={() => navigate("/settings/usage")}>{t("viewUsage")}</BannerAction>;
  if (level === "blocked") {
    return (
      <Banner
        variant="error"
        icon={<CircleAlert size={14} />}
        message={t("reached", { limit: formatCost(budget.monthlyLimitUsd), date })}
        action={action}
      />
    );
  }
  if (dismissedFor === budget.resetsAt || readDismissed(budget.resetsAt)) return null;
  return (
    <Banner
      variant="warning"
      icon={<Gauge size={14} />}
      message={t("warning", {
        percent: Math.round(budget.percentUsed),
        spent: formatCost(budget.currentSpendUsd),
        limit: formatCost(budget.monthlyLimitUsd),
        date,
      })}
      action={action}
      dismissLabel={t("dismiss")}
      onDismiss={() => {
        try {
          sessionStorage.setItem(DISMISS_KEY, budget.resetsAt);
        } catch {
          // Storage unavailable: still hide it for this page view.
        }
        setDismissedFor(budget.resetsAt);
      }}
    />
  );
}
